/**
 * DuplicateMediaPanel
 *
 * Duplicate-media findings on a claim: which photo was seen before, where, and what
 * the reviewer concluded about it.
 *
 * Shaped to sit alongside the potential-duplicate callout and read as the same
 * kind of thing, because it is - "have we seen this before" is one question
 * whether the match was on a photograph or an email address.
 *
 * Two deliberate departures from a plain warning banner:
 *
 * 1. The photos are side by side. A finding that says "this is a duplicate"
 *    without showing you what it matched is not reviewable, it is just an
 *    instruction to go looking.
 * 2. Closing it records a JUDGEMENT, not an acknowledgement. The outcomes come
 *    from the tenant's catalogue, so the wording is theirs, and each carries a
 *    platform intent so a rename cannot break what the verdict meant.
 */

import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  Check,
  ExternalLink,
  ImageOff,
  Maximize2,
  ScanSearch,
} from "lucide-react";
import { toast } from "sonner";

import { FileViewerDialog } from "@/shared/components/file-viewer-dialog";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";

export interface ImageReviewOutcome {
  key: string;
  label: string;
  description?: string;
  intent: string;
  requires_note: boolean;
}

export interface ImageReviewFile {
  uuid?: string;
  file_name?: string;
  file_url?: string;
  requirement_key?: string;
  captured_at?: string;
}

export interface ImageReviewClaimCard {
  uuid: string;
  claim_number?: string;
  created_at?: string;
  customer_name?: string;
  product_name?: string;
}

export interface ImageReviewArchiveCard {
  reference?: string;
  recorded_outcome?: string;
  captured_on?: string;
  prior_system?: string;
}

/**
 * Where the matched image came from.
 *
 * A match against the imported historical library has no claim to open. Left
 * undistinguished it renders as a photo with a blank card, which reads as a
 * broken panel rather than as the nature of the record - so the origin is
 * explicit, and the backend supplies the sentence that explains it.
 */
export interface ImageReviewOrigin {
  kind: "claim" | "archive" | "unknown";
  label: string;
  rationale: string;
  claim: ImageReviewClaimCard | null;
  archive: ImageReviewArchiveCard | null;
}

export interface ImageReviewMatch {
  uuid: string;
  confidence: string;
  severity: string;
  status: string;
  resolution?: string;
  resolution_note?: string;
  reviewed: boolean;
  submitted_image: ImageReviewFile;
  matched_image: ImageReviewFile;
  matched_origin: ImageReviewOrigin;
  /** Same object as `matched_origin.claim`; kept for older callers. */
  matched_claim: ImageReviewClaimCard | null;
}

export interface ImageReview {
  total: number;
  unresolved: number;
  outcomes: ImageReviewOutcome[];
  matches: ImageReviewMatch[];
}

interface Props {
  review: ImageReview | null | undefined;
  /** Opens the read-only claim preview in place, as the duplicate callout does. */
  onPreviewClaim?: (claimUuid: string) => void;
  onResolve: (args: {
    matchUuid: string;
    outcome: string;
    note: string;
  }) => Promise<void>;
  canResolve?: boolean;
  isResolving?: boolean;
}

function ArchiveCard({ archive }: { archive: ImageReviewArchiveCard }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-0.5 rounded-[6px] bg-[var(--bg-subtle)] px-2.5 py-2">
      <span className="text-[11px] font-medium">
        {archive.reference
          ? t(
              "claims.mediaReview.archiveReference",
              "Reference {{reference}}",
              {
                reference: archive.reference,
              },
            )
          : t("claims.mediaReview.historicalRecord", "Historical record")}
      </span>
      {archive.captured_on ? (
        <span className="text-[11px] text-[var(--ink-3)]">
          {archive.captured_on}
        </span>
      ) : null}
      {archive.recorded_outcome ? (
        <span className="text-[11px] text-[var(--ink-3)]">
          {t(
            "claims.mediaReview.previouslyRecordedAs",
            "Previously recorded as {{outcome}}",
            { outcome: archive.recorded_outcome },
          )}
        </span>
      ) : null}
      {/* No link, and the reason for it said out loud. */}
      <span className="text-[11px] text-[var(--ink-3)]">
        {t(
          "claims.mediaReview.noClaimRecord",
          "No claim record in the platform",
        )}
      </span>
    </div>
  );
}

function ClaimCard({
  claim,
  onPreviewClaim,
}: {
  claim: ImageReviewClaimCard;
  onPreviewClaim?: (claimUuid: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-0.5 rounded-[6px] bg-[var(--bg-subtle)] px-2.5 py-2">
      {claim.customer_name ? (
        <span className="text-[12px] font-medium text-[var(--ink)]">
          {claim.customer_name}
        </span>
      ) : null}
      {claim.product_name ? (
        <span className="text-[11px] text-[var(--ink-3)]">
          {claim.product_name}
        </span>
      ) : null}
      {onPreviewClaim ? (
        <Button
          variant="link"
          onClick={() => onPreviewClaim(claim.uuid)}
          className="mt-1 inline-flex h-auto cursor-pointer items-center justify-start gap-1 p-0 text-[11px] hover:underline"
        >
          {claim.claim_number ||
            t("claims.mediaReview.viewClaim", "View claim")}
          <ExternalLink className="h-3 w-3 shrink-0" />
        </Button>
      ) : claim.claim_number ? (
        <span className="mt-1 text-[11px] font-medium">
          {claim.claim_number}
        </span>
      ) : null}
    </div>
  );
}

function ImagePane({
  title,
  file,
  onEnlarge,
}: {
  title: string;
  file: ImageReviewFile;
  onEnlarge: (file: ImageReviewFile) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
      <span className="text-[10px] font-medium tracking-[0.08em] text-[var(--ink-3)] uppercase">
        {title}
      </span>
      {/* Both panes are the same fixed height so the pair is genuinely
        comparable - a resized copy must not read as a different photo simply
        because it renders smaller. */}
      {file?.file_url ? (
        <Button
          variant="ghost"
          onClick={() => onEnlarge(file)}
          aria-label={t("claims.mediaReview.enlarge", "Enlarge {{name}}", {
            name: file.file_name ?? title,
          })}
          className="group relative flex min-h-[240px] flex-1 w-full cursor-zoom-in items-center justify-center overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-0 hover:bg-[var(--bg-subtle)]"
        >
          <img
            src={file.file_url}
            alt={file.file_name ?? title}
            className="max-h-full max-w-full object-contain"
          />
          <span className="absolute right-2 bottom-2 flex h-6 w-6 items-center justify-center rounded-[4px] bg-[var(--bg)] text-[var(--ink-2)] opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
            <Maximize2 className="h-3 w-3" />
          </span>
        </Button>
      ) : (
        <div className="flex min-h-[240px] flex-1 flex-col items-center justify-center gap-1 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-3)]">
          <ImageOff className="h-4 w-4" />
          <span className="text-[11px]">
            {t("claims.mediaReview.imageUnavailable", "Image unavailable")}
          </span>
        </div>
      )}
      <span className="truncate text-[11px] text-[var(--ink-3)]">
        {file?.file_name ?? "-"}
      </span>
    </div>
  );
}

/**
 * One phrase naming what this photo matched, for the collapsed row.
 *
 * Reads from the origin so an archive hit never claims to be a claim the
 * reviewer could go and open.
 */
function describeWhere(match: ImageReviewMatch, t: TFunction): string {
  const origin = match.matched_origin;
  if (origin?.kind === "archive") {
    return origin.archive?.reference
      ? t(
          "claims.mediaReview.matchesLibraryRef",
          "matches the historical library ({{reference}})",
          { reference: origin.archive.reference },
        )
      : t(
          "claims.mediaReview.matchesLibrary",
          "matches the historical library",
        );
  }
  if (origin?.claim) {
    // Tenants that do not number their claims still need the row to identify
    // the other one, so fall back to the customer rather than a bare noun.
    const who = origin.claim.claim_number || origin.claim.customer_name;
    return who
      ? t(
          "claims.mediaReview.matchesClaimFrom",
          "matches an earlier claim from {{who}}",
          { who },
        )
      : t("claims.mediaReview.matchesClaim", "matches an earlier claim");
  }
  return t("claims.mediaReview.matchesImage", "matched an earlier image");
}

export function DuplicateMediaPanel({
  review,
  onPreviewClaim,
  onResolve,
  canResolve = true,
  isResolving = false,
}: Props) {
  const { t } = useTranslation();
  const [openMatch, setOpenMatch] = useState<ImageReviewMatch | null>(null);
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [enlarged, setEnlarged] = useState<ImageReviewFile | null>(null);

  // Memoised so the lookup below does not see a fresh array every render.
  const outcomes = useMemo(() => review?.outcomes ?? [], [review]);
  const selected = useMemo(
    () => outcomes.find((o) => o.key === outcome),
    [outcomes, outcome],
  );
  const noteRequired = Boolean(selected?.requires_note);

  if (!review || review.matches.length === 0) return null;

  const openComparison = (match: ImageReviewMatch) => {
    setOpenMatch(match);
    setOutcome(match.resolution ?? "");
    setNote(match.resolution_note ?? "");
  };

  const submit = async () => {
    if (!openMatch) return;
    // The button stays enabled on an incomplete form; the click validates and
    // says what is missing, rather than leaving the reviewer hunting.
    if (!outcome) {
      toast.error(
        t(
          "claims.mediaReview.chooseOutcomeError",
          "Choose what you found before saving.",
        ),
      );
      return;
    }
    if (noteRequired && !note.trim()) {
      toast.error(
        t(
          "claims.mediaReview.noteRequiredError",
          "This outcome needs a short note.",
        ),
      );
      return;
    }
    await onResolve({ matchUuid: openMatch.uuid, outcome, note });
    setOpenMatch(null);
  };

  return (
    <>
      <div className="mb-4 flex flex-col gap-2 rounded-[6px] border-[0.5px] border-[var(--status-warn)] bg-[var(--bg-subtle)] px-4 py-2.5">
        <div className="flex items-start gap-2">
          <ScanSearch className="mt-px h-3 w-3 shrink-0 text-[var(--status-warn)]" />
          <span className="text-[11px] text-[var(--ink-2)]">
            {review.total === 1
              ? t(
                  "claims.mediaReview.summaryOne",
                  "Evidence check - 1 photo on this claim has been seen before",
                )
              : t(
                  "claims.mediaReview.summaryMany",
                  "Evidence check - {{count}} photos on this claim have been seen before",
                  { count: review.total },
                )}
            {/* Only say how many are outstanding when some are. "(0 not yet
              resolved)" is noise, and it reads as a defect once the reviewer
              has just settled the last one. */}
            {review.unresolved > 0 && review.unresolved !== review.total
              ? ` ${t(
                  "claims.mediaReview.unresolvedCount",
                  "({{count}} not yet resolved)",
                  { count: review.unresolved },
                )}`
              : ""}
            .{" "}
            {review.unresolved === 0
              ? t("claims.mediaReview.allReviewed", "All reviewed.")
              : t(
                  "claims.mediaReview.compareBeforeDeciding",
                  "Compare them before deciding.",
                )}
          </span>
        </div>

        <div className="flex flex-col gap-1.5 pl-5">
          {review.matches.map((match) => (
            <div key={match.uuid} className="flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  match.severity === "FAIL" ? "destructive" : "secondary"
                }
                className="text-[10px]"
              >
                {match.confidence}
              </Badge>
              <span className="text-[11px] text-[var(--ink-3)]">
                {describeWhere(match, t)}
              </span>
              <Button
                variant="link"
                onClick={() => openComparison(match)}
                className="inline-flex h-auto cursor-pointer items-center gap-1 p-0 text-[11px] text-[var(--status-warn)] hover:underline"
              >
                {t("claims.mediaReview.compare", "Compare")}
              </Button>
              {match.reviewed ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-[var(--ink-3)]">
                  <Check className="h-3 w-3" />
                  {outcomes.find((o) => o.key === match.resolution)?.label ??
                    t("claims.mediaReview.resolved", "Resolved")}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <Dialog
        open={Boolean(openMatch)}
        onOpenChange={(next) => !next && setOpenMatch(null)}
      >
        {/* Same footprint as the file viewer. `sm:` is needed: the base
          DialogContent caps at sm:max-w-sm. */}
        <DialogContent className="h-[95vh] w-[95vw] sm:max-w-[95vw]">
          <DialogHeader>
            <DialogTitle>{openMatch?.confidence}</DialogTitle>
            <DialogDescription>
              {openMatch?.matched_origin?.rationale ? (
                <>{openMatch.matched_origin.rationale} </>
              ) : null}
              {t(
                "claims.mediaReview.instructions",
                "Compare the two, then record what you found. This never declines a claim on its own.",
              )}
            </DialogDescription>
          </DialogHeader>

          {openMatch ? (
            // Photos take the height; the decision sits beside them so it is
            // never pushed below the fold. Scrolls on very short screens.
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto md:flex-row">
              <div className="flex min-h-0 min-w-0 flex-1 gap-4">
                <ImagePane
                  title={t("claims.mediaReview.onThisClaim", "On this claim")}
                  file={openMatch.submitted_image}
                  onEnlarge={setEnlarged}
                />
                <ImagePane
                  title={
                    openMatch.matched_origin?.label ??
                    t("claims.mediaReview.earlierClaim", "Earlier claim")
                  }
                  file={openMatch.matched_image}
                  onEnlarge={setEnlarged}
                />
              </div>

              <div className="flex flex-col gap-4 md:w-[280px] md:shrink-0">
                {openMatch.matched_origin?.claim ||
                openMatch.matched_origin?.archive ? (
                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] font-medium tracking-[0.08em] text-[var(--ink-3)] uppercase">
                      {openMatch.matched_origin.label ??
                        t("claims.mediaReview.earlierClaim", "Earlier claim")}
                    </span>
                    {openMatch.matched_origin.claim ? (
                      <ClaimCard
                        claim={openMatch.matched_origin.claim}
                        onPreviewClaim={onPreviewClaim}
                      />
                    ) : openMatch.matched_origin.archive ? (
                      <ArchiveCard archive={openMatch.matched_origin.archive} />
                    ) : null}
                  </div>
                ) : null}

                {canResolve ? (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="image-review-outcome">
                      {t(
                        "claims.mediaReview.whatDidYouFind",
                        "What did you find?",
                      )}
                    </Label>
                    <Select value={outcome} onValueChange={setOutcome}>
                      <SelectTrigger id="image-review-outcome">
                        <SelectValue
                          placeholder={t(
                            "claims.mediaReview.chooseOutcome",
                            "Choose an outcome",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {outcomes.map((o) => (
                          <SelectItem key={o.key} value={o.key}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selected?.description ? (
                      <span className="text-[11px] text-[var(--ink-3)]">
                        {selected.description}
                      </span>
                    ) : null}
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={
                        noteRequired
                          ? t(
                              "claims.mediaReview.noteRequiredPlaceholder",
                              "Required - what made you decide that?",
                            )
                          : t(
                              "claims.mediaReview.noteOptionalPlaceholder",
                              "Optional note",
                            )
                      }
                      rows={4}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenMatch(null)}>
              {t("claims.mediaReview.close", "Close")}
            </Button>
            {canResolve ? (
              <Button onClick={submit} disabled={isResolving}>
                {t("claims.mediaReview.saveOutcome", "Save outcome")}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FileViewerDialog
        file={
          enlarged?.file_url
            ? {
                file_uuid: enlarged.uuid,
                file_url: enlarged.file_url,
                file_name: enlarged.file_name ?? "",
              }
            : null
        }
        onOpenChange={(next) => !next && setEnlarged(null)}
      />
    </>
  );
}
